import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ChefHat } from 'lucide-react';

export const ChineseDumplings: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Homemade Chinese Dumplings (Jiaozi) Recipe | Chinese Food Maps</title>
        <meta name="description" content="Learn how to make authentic Chinese dumplings from scratch. Master the art of making the perfect dumpling dough, filling, and pleating techniques." />
        <meta property="og:title" content="Homemade Chinese Dumplings (Jiaozi) Recipe | Chinese Food Maps" />
        <meta property="og:description" content="Learn how to make authentic Chinese dumplings from scratch." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://chinesefoodmaps.com/recipes/chinese-dumplings" />
        <meta property="og:image" content="https://cdn-icons-png.flaticon.com/512/2548/2548242.png" />
        <link rel="canonical" href="https://chinesefoodmaps.com/recipes/chinese-dumplings" />
      </Helmet>
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-6 text-red-900">Homemade Chinese Dumplings (Jiaozi) Recipe</h1>
        
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="prose max-w-none text-gray-600">
            <p className="text-lg leading-relaxed mb-6">
              Chinese dumplings (饺子) are a staple of Chinese cuisine, especially during festivals and family gatherings. This recipe will teach you how to make them from scratch, including the wrapper dough and filling.
            </p>

            <h3 className="text-xl font-semibold text-red-800 mb-4">For the Dumpling Dough:</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>3 cups all-purpose flour</li>
              <li>1 cup warm water</li>
              <li>½ tsp salt</li>
              <li>Extra flour for dusting</li>
            </ul>

            <h3 className="text-xl font-semibold text-red-800 mb-4">For the Filling:</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>1 lb ground pork</li>
              <li>2 cups Chinese cabbage, finely chopped</li>
              <li>2 green onions, minced</li>
              <li>1 tbsp ginger, minced</li>
              <li>2 cloves garlic, minced</li>
              <li>2 tbsp soy sauce</li>
              <li>1 tbsp sesame oil</li>
              <li>1 tsp white pepper</li>
              <li>½ tsp salt</li>
            </ul>

            

            <h3 className="text-xl font-semibold text-red-800 mb-4">For the Dipping Sauce:</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>¼ cup black vinegar</li>
              <li>¼ cup soy sauce</li>
              <li>1 tbsp chili oil (optional)</li>
              <li>Minced garlic and ginger to taste</li>
            </ul>

            <h3 className="text-xl font-semibold text-red-800 mb-4">Instructions:</h3>
            <ol className="list-decimal pl-6 mb-6 space-y-4">
              <li>
                <strong>Make Dough:</strong> Mix flour and salt, gradually add water while kneading until smooth.
              </li>
              <li>
                <strong>Rest Dough:</strong> Cover and rest for 30 minutes.
              </li>
              <li>
                <strong>Prepare Filling:</strong> Mix all filling ingredients until well combined.
              </li>
              <li>
                <strong>Make Wrappers:</strong> Roll dough into a log, cut into pieces, roll each into circles.
              </li>
              <li>
                <strong>Fill and Pleat:</strong> Place filling in center, pleat edges to seal.
              </li>
              <li>
                <strong>Cook:</strong> Boil in water until they float, then cook 2-3 minutes more.
              </li>
            </ol>

            <div className="bg-yellow-50 rounded-lg p-6 mt-8">
              <h4 className="text-lg font-semibold text-red-800 mb-2">Pro Tips from Chinese Food Maps:</h4>
              <ul className="list-disc pl-6 space-y-2">
                <li>Salt the cabbage and squeeze out excess water</li>
                <li>Keep unused dough covered while working</li>
                <li>Don't overfill the dumplings</li>
                <li>Test one dumpling first to check seasoning</li>
                <li>Freeze uncooked dumplings for later use</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};